import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MENU_ITENS } from '@core/static/menus';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';


export interface MenuTag {
  color: string; // Background Color
  value: string;
}

export interface MenuChildrenItem {
  route: string;
  name: string;
  type: 'link' | 'sub' | 'extLink' | 'extTabLink';
  children?: MenuChildrenItem[];
}

export interface Menu {
  route: string;
  name: string;
  type: 'link' | 'sub' | 'extLink' | 'extTabLink';
  icon: string;
  label?: MenuTag;
  badge?: MenuTag;
  children?: MenuChildrenItem[];
  visible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  public menu: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>([]);
  public routeMenuSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  constructor(
    private router: Router,
  ) {
    let routeMenu = 'menuPublic';
    let namespace = 'menu';
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
      switch (ev.urlAfterRedirects.includes('/')) {
        case ev.urlAfterRedirects.includes('/public'):
          routeMenu = 'menuPublic';
          namespace = 'menu';
          break;
        case ev.urlAfterRedirects.includes('/home'):
          routeMenu = 'menuHome';
          namespace = 'menu';
          break;
        case ev.urlAfterRedirects.includes('/admin'):
          routeMenu = 'menuAdmin';
          namespace = 'menu';
          break;
      }
      this.menu.next([]);
      this.recursMenuForTranslation(MENU_ITENS[routeMenu], namespace, routeMenu);
    });
  }

  getAll(): Observable<Menu[]> {
    return this.menu.asObservable();
  }

  set(menu: Menu[]): Observable<Menu[]> {
    this.menu.next(menu);
    return this.menu.asObservable();
  }

  add(menu: Menu) {
    const tmpMenu = this.menu.value;
    tmpMenu.push(menu);
    this.menu.next(tmpMenu);
  }

  reset() {
    this.menu.next([]);
  }

  getMenuItemName(routeArr: string[]): string {
    return this.getMenuLevel(routeArr)[routeArr.length - 1];
  }

  /** Menu level */

  private isLeafItem(item: MenuChildrenItem): boolean {
    //// if a menuItem is leaf
    const cond0 = item.route === undefined;
    const cond1 = item.children === undefined;
    const cond2 = !cond1 && item.children.length === 0;
    return cond0 || cond1 || cond2;
  }

  private deepcopyJsonObj(jobj: any): any {
    //// deepcop object-could-be-jsonized
    return JSON.parse(JSON.stringify(jobj));
  }

  private jsonObjEqual(jobj0: any, jobj1: any): boolean {
    //// if two objects-could-be-jsonized equal
    const cond = JSON.stringify(jobj0) === JSON.stringify(jobj1);
    return cond;
  }

  private routeEqual(routeArr: Array<string>, realRouteArr: Array<string>): boolean {
    //// if routeArr equals realRouteArr(after remove empty-route-element)
    realRouteArr = this.deepcopyJsonObj(realRouteArr);
    realRouteArr = realRouteArr.filter(r => r !== '');
    return this.jsonObjEqual(routeArr, realRouteArr);
  }

  getMenuLevel(routeArr: string[]): string[] {
    let tmpArr = [];
    this.menu.value.forEach(item => {
      //// breadth-first-traverse -modified
      let unhandledLayer = [{ item, parentNamePathList: [], realRouteArr: [] }];
      while (unhandledLayer.length > 0) {
        let nextUnhandledLayer = [];
        for (const ele of unhandledLayer) {
          const eachItem = ele.item;
          const currentNamePathList = this.deepcopyJsonObj(ele.parentNamePathList).concat(
            eachItem.name
          );
          const currentRealRouteArr = this.deepcopyJsonObj(ele.realRouteArr).concat(
            eachItem.route
          );
          //// compare the full Array
          //// for expandable
          const cond = this.routeEqual(routeArr, currentRealRouteArr);
          if (cond) {
            tmpArr = currentNamePathList;
            break;
          }
          ////
          const isLeafCond = this.isLeafItem(eachItem);
          if (!isLeafCond) {
            const children = eachItem.children;
            const wrappedChildren = children.map(child => ({
              item: child,
              parentNamePathList: currentNamePathList,
              realRouteArr: currentRealRouteArr,
            }));
            nextUnhandledLayer = nextUnhandledLayer.concat(wrappedChildren);
          }
        }
        unhandledLayer = nextUnhandledLayer;
      }
    });
    return tmpArr;
  }

  /** Menu for translation */
  recursMenuForTranslation(menu: Menu[] | MenuChildrenItem[], namespace: string, routeMenu: string) {
    // this.menu.next([]);
    if (menu && menu.length > 0) {
      menu.forEach(menuItem => {
        if (!menuItem.name.includes(namespace)) {
          menuItem.name = `${namespace}.${menuItem.name}`;
          if (menuItem.children && menuItem.children.length > 0) {
            this.recursMenuForTranslation(menuItem.children, menuItem.name, routeMenu);
          }
        }
      });
    }
    this.menu.next(MENU_ITENS[routeMenu]);
    this.routeMenuSubject.next(routeMenu);
  }
}
